'use client'

import { useCallback, useMemo, useSyncExternalStore } from 'react'
import { z } from 'zod'
import {
  CART_LINE_MAX_QUANTITY,
  CART_MAX_LINES,
  CartLineFieldsSchema,
  CartLineSchema,
  priceCartLineCents,
  type CartLine,
} from '@mist/contracts/commerce'
import { COLLECTION_PRICE_USD, EDITION_PRICE_USD } from '@mist/content/collection'

/**
 * The bag (R-BRAND-02, R-ARCH-06). A dependency-free external store read with
 * `useSyncExternalStore` and mirrored to localStorage under `mist.cart.v1`.
 *
 * Server and first client render always see the empty bag: the persisted bag is
 * loaded only once the first subscriber mounts (a passive effect), so hydration
 * never mismatches. Every storage access is guarded — private windows, blocked
 * site data and quota errors degrade to an in-memory bag. Prices are never
 * trusted from storage; they are re-derived from `@mist/content` on load so a
 * stale bag can never display an old amount. Engraving and signature text live
 * only here and in the checkout request body — never in analytics.
 */
export const CART_STORAGE_KEY = 'mist.cart.v1'

export type CartItem = CartLine & {
  id: string
  title: string
  details: string[]
  unitAmountCents: number
}

export type CartAddInput = Omit<CartItem, 'id' | 'quantity'> & { quantity?: number }

export type CartSnapshot = {
  readonly items: readonly CartItem[]
  readonly open: boolean
}

export type CartApi = {
  readonly items: readonly CartItem[]
  readonly count: number
  readonly subtotalCents: number
  readonly open: boolean
  add(input: CartAddInput): void
  remove(id: string): void
  clear(): void
  setOpen(value: boolean): void
}

const PRICES = { bottle: COLLECTION_PRICE_USD, edition: EDITION_PRICE_USD } as const

const EMPTY: CartSnapshot = Object.freeze({ items: Object.freeze([]) as readonly CartItem[], open: false })

const StoredItemSchema = CartLineFieldsSchema.extend({
  id: z.string().min(1).max(80),
  title: z.string().min(1).max(160),
  details: z.array(z.string().max(200)).max(8),
})

let snapshot: CartSnapshot = EMPTY
let loaded = false
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function setSnapshot(next: CartSnapshot, persist: boolean): void {
  snapshot = next
  if (persist) writeStorage(next.items)
  emit()
}

function readStorage(): CartItem[] {
  if (typeof window === 'undefined') return []
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(CART_STORAGE_KEY)
  } catch {
    return []
  }
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const items: CartItem[] = []
    for (const candidate of parsed.slice(0, CART_MAX_LINES)) {
      const stored = StoredItemSchema.safeParse(candidate)
      if (!stored.success || !CartLineSchema.safeParse(stored.data).success) continue
      items.push({ ...stored.data, unitAmountCents: priceCartLineCents(stored.data, PRICES) })
    }
    return items
  } catch {
    return []
  }
}

function writeStorage(items: readonly CartItem[]): void {
  if (typeof window === 'undefined') return
  try {
    if (items.length === 0) window.localStorage.removeItem(CART_STORAGE_KEY)
    else window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items.map(({ unitAmountCents: _unit, ...rest }) => rest)))
  } catch {
    // Storage is a convenience mirror; the in-memory bag stays authoritative.
  }
}

function loadOnce(): void {
  if (loaded) return
  loaded = true
  const items = readStorage()
  if (items.length > 0) setSnapshot({ items, open: snapshot.open }, false)
}

function onStorage(event: StorageEvent): void {
  if (event.key !== null && event.key !== CART_STORAGE_KEY) return
  setSnapshot({ items: readStorage(), open: snapshot.open }, false)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (listeners.size === 1 && typeof window !== 'undefined') window.addEventListener('storage', onStorage)
  loadOnce()
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage)
  }
}

function getSnapshot(): CartSnapshot {
  return snapshot
}

function getServerSnapshot(): CartSnapshot {
  return EMPTY
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `bag_${crypto.randomUUID()}`
  return `bag_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

/** Two lines are the same bag row when everything the buyer chose matches. */
export function cartLineKey(line: CartLine): string {
  return [line.kind, line.month ?? '', line.line ?? '', line.editionId ?? '', line.engraving ?? '', line.signature ?? ''].join('|')
}

function normalizeLine(input: CartAddInput): CartLine | null {
  const candidate: Record<string, unknown> = { kind: input.kind, quantity: input.quantity ?? 1 }
  if (input.month) candidate['month'] = input.month
  if (input.line) candidate['line'] = input.line
  if (input.editionId) candidate['editionId'] = input.editionId
  if (input.engraving?.trim()) candidate['engraving'] = input.engraving.trim()
  if (input.signature?.trim()) candidate['signature'] = input.signature.trim()
  const parsed = CartLineSchema.safeParse(candidate)
  return parsed.success ? parsed.data : null
}

/** Adds a line, merging into an identical row by incrementing its quantity. */
export function addCartItem(input: CartAddInput): void {
  loadOnce()
  const line = normalizeLine(input)
  if (!line) return
  const key = cartLineKey(line)
  const existing = snapshot.items.find((item) => cartLineKey(item) === key)
  let items: CartItem[]
  if (existing) {
    const quantity = Math.min(CART_LINE_MAX_QUANTITY, existing.quantity + line.quantity)
    items = snapshot.items.map((item) => (item === existing ? { ...item, quantity } : item))
  } else {
    if (snapshot.items.length >= CART_MAX_LINES) return
    items = [
      ...snapshot.items,
      {
        ...line,
        id: newId(),
        title: input.title,
        details: [...input.details],
        unitAmountCents: priceCartLineCents(line, PRICES),
      },
    ]
  }
  setSnapshot({ items, open: snapshot.open }, true)
}

export function removeCartItem(id: string): void {
  loadOnce()
  const items = snapshot.items.filter((item) => item.id !== id)
  if (items.length === snapshot.items.length) return
  setSnapshot({ items, open: snapshot.open }, true)
}

export function clearCart(): void {
  loadOnce()
  if (snapshot.items.length === 0) return
  setSnapshot({ items: [], open: snapshot.open }, true)
}

export function setCartOpen(value: boolean): void {
  if (snapshot.open === value) return
  setSnapshot({ items: snapshot.items, open: value }, false)
}

export function readCartSnapshot(): CartSnapshot {
  return snapshot
}

export function cartCount(items: readonly CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0)
}

export function cartSubtotalCents(items: readonly CartItem[]): number {
  return items.reduce((sum, item) => sum + item.unitAmountCents * item.quantity, 0)
}

/**
 * Forgets the in-memory bag and the loaded flag so the next subscriber reads
 * storage again. For tests and sign-out; storage itself is left alone.
 */
export function resetCartStore(): void {
  snapshot = EMPTY
  loaded = false
  emit()
}

/** `24000` → `$240`; `24050` → `$240.50`. */
export function formatUsd(cents: number): string {
  const whole = Math.round(Math.abs(cents))
  const dollars = Math.floor(whole / 100)
  const remainder = whole % 100
  const sign = cents < 0 ? '-' : ''
  const grouped = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${sign}$${grouped}${remainder ? `.${String(remainder).padStart(2, '0')}` : ''}`
}

export function useCart(): CartApi {
  const current = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const add = useCallback((input: CartAddInput) => addCartItem(input), [])
  const remove = useCallback((id: string) => removeCartItem(id), [])
  const clear = useCallback(() => clearCart(), [])
  const setOpen = useCallback((value: boolean) => setCartOpen(value), [])
  return useMemo(
    () => ({
      items: current.items,
      count: cartCount(current.items),
      subtotalCents: cartSubtotalCents(current.items),
      open: current.open,
      add,
      remove,
      clear,
      setOpen,
    }),
    [current, add, remove, clear, setOpen],
  )
}
